// Fleet Overview (fleet-dash): what "Needs you today" and "Alerts & updates" show. Plain JS, no JSX, so tests/fleet-dash.test.mjs can import it.
// One rule: a thing appears in one place. Expired and expiring documents, overdue maintenance and approvals are in Needs you today; the list below has the rest.

// documents = ship documents, surveys on them and crew certificates (fleetCore dueSoon kinds)
export const DOC_KINDS = ['doc', 'survey', 'crew']
export const SHOW_FIRST = 5

const onVessels = rows => {
  const n = new Set(rows.map(r => String(r.vid))).size
  return 'on ' + n + (n === 1 ? ' vessel' : ' vessels')
}

// the lines of the card, in a fixed order; a line with nothing to do is left out. Red only for overdue / expired.
// approvals = { count, total } or null; overdue / expired / expiring = rows with vid; jobs = { today, overdue, waiting } or null (Service package off: null)
export function needsLines({ approvals = null, overdue = [], expired = [], expiring = [], outOfStock = [], jobs = null }) {
  const out = []
  if (approvals && approvals.count > 0) out.push({ key: 'approvals', count: approvals.count, label: 'Approvals', detail: approvals.total || 'waiting', tone: 'blue', action: 'Review' })
  if (overdue.length) out.push({ key: 'overdue', count: overdue.length, label: 'Overdue maintenance', detail: onVessels(overdue), tone: 'red', action: 'View' })
  if (expired.length || expiring.length) {
    const say = [expired.length ? expired.length + ' expired' : '', expiring.length ? expiring.length + ' expiring in 30 days' : ''].filter(Boolean).join(' · ')
    out.push({ key: 'docs', count: expired.length + expiring.length, label: 'Documents', detail: say, tone: expired.length ? 'red' : 'amber', action: 'View' })
  }
  if (outOfStock.length) out.push({ key: 'stock', count: outOfStock.length, label: 'Out of stock', detail: onVessels(outOfStock), tone: 'amber', action: 'View' })
  if (jobs && (jobs.today || jobs.overdue || jobs.waiting)) {
    const say = [jobs.overdue ? jobs.overdue + ' overdue' : '', jobs.waiting ? jobs.waiting + ' waiting for owner' : ''].filter(Boolean).join(' · ')
    out.push({ key: 'jobs', count: jobs.today, label: 'Jobs today', detail: say, tone: 'blue', action: 'Open Jobs' })
  }
  return out
}

// Documents expiring within 30 days, for that line of the card
export const expiringDocs = dueItems => dueItems.filter(x => DOC_KINDS.includes(x.kind) && x.window === 30)

// Alerts + Updates for you, as one list: overdue first (red), then what is out of service and the unread updates, then what falls due, soonest first.
// vessels = [{ id, vessel_name, c: the vessel's compliance row or undefined }]; notes = unread app_notices
export function alertRows(vessels, notes = []) {
  const red = [], mid = [], due = []
  for (const v of vessels) {
    const c = v.c
    if (!c) continue
    const base = { vid: v.id, vessel: v.vessel_name }
    for (const x of c.drillsLateL || []) red.push({ ...base, key: 'dl' + v.id + x.name, tone: 'red', title: 'Overdue: ' + x.name })
    for (const x of c.raLateL || []) red.push({ ...base, key: 'ra' + v.id + x.name, tone: 'red', title: 'RA review overdue: ' + x.name, date: x.date })
    for (const x of c.oosL || []) mid.push({ ...base, key: 'os' + v.id + x.name, tone: 'amber', title: 'Out of service: ' + x.name })
    const dueL = c.dueL || [], drills = dueL.filter(x => x.kind === 'drill')
    // several drills due this month: one line per vessel, not one per drill
    if (drills.length > 1) due.push({ ...base, key: 'dd' + v.id, tone: 'amber', days: Math.min(...drills.map(x => x.days)), title: drills.length + ' drills due this month: ' + drills.map(x => x.name.replace(/ drill$/, '')).join(', ') })
    for (const x of dueL) {
      if (x.kind === 'drill' && drills.length > 1) continue
      if (DOC_KINDS.includes(x.kind) && x.window === 30) continue   // on the Documents line above
      due.push({ ...base, key: 'du' + v.id + x.kind + x.label, tone: 'amber', days: x.days, title: x.label, date: x.date })
    }
  }
  const updates = notes.map(n => ({ key: 'n' + n.id, note: n, tone: /rejected|survey_due/.test(n.kind) ? 'amber' : 'blue', vessel: (n.data && n.data.vessel) || '', title: n.title, when: n.created_at }))
  return [...red, ...mid, ...updates, ...due.sort((a, b) => a.days - b.days)]
}
